/* eslint-disable react/prop-types */
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useCallback } from "react";
import { ChevronDown } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { dashboardLinks } from "@/components/lib/SidebarLinks";
import DashboardRangeTabs from "@/components/dashboard/dashboard-range-tabs";
import { Skeleton } from "@/components/ui/skeleton";
import useAuth from "@/hooks/useAuth";
import { fetchMenuReports } from "@/redux/slice/dashboard/dashboard-slice";
import { formatCurrency } from "@/lib/utils";

const COLORS = {
  gold: "#FBBF24",
  green: "#10B981",
  red: "#EF4444",
  indigo: "#6366F1",
  cyan: "#22D3EE",
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const StatCard = ({ title, value, color, loading }) => (
  <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-5 md:p-6 h-[130px] md:h-[150px] flex flex-col justify-between">
    <p className="text-gray-300 text-sm md:text-base font-medium">{title}</p>
    {loading ? (
      <Skeleton className="h-8 w-24" />
    ) : (
      <p className="text-2xl md:text-3xl font-semibold" style={{ color }}>
        {Number(value || 0).toLocaleString()}
      </p>
    )}
  </div>
);

const MenuCard = ({ item, loading, currency }) => (
  <div className="bg-[#1A1A1A] border border-white/5 rounded-lg p-5 md:p-6 space-y-4 min-h-[170px]">
    {loading ? (
      <>
        <Skeleton className="h-6 w-40" />
        <div className="space-y-2.5">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-40" />
        </div>
      </>
    ) : (
      <>
        <h3 className="text-[#FBBF24] font-medium text-base md:text-lg leading-tight">
          {item?.menu_name || "Menu"}
        </h3>
        <div className="space-y-2.5">
          <div className="flex text-sm">
            <span className="text-gray-400 w-24">Total Item</span>
            <span className="text-white">
              : {Number(item?.total_items || 0).toLocaleString()}
            </span>
          </div>
          <div className="flex text-sm">
            <span className="text-gray-400 w-24">Total Sale</span>
            <span className="text-white">
              : {Number(item?.total_sale || 0).toLocaleString()}
            </span>
          </div>
          <div className="flex text-sm">
            <span className="text-gray-400 w-24">Revenue</span>
            <span className="text-white">
              : {formatCurrency(item?.revenue || 0, currency)}
            </span>
          </div>
        </div>
      </>
    )}
  </div>
);

export default function MenuReports() {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const yearOptions = useMemo(
    () => [currentYear, currentYear - 1],
    [currentYear],
  );

  const recentMonths = useMemo(
    () =>
      [2, 1, 0].map((offset) => {
        const date = new Date(currentYear, currentMonth - 1 - offset, 1);
        return {
          value: date.getMonth() + 1,
          label: MONTH_NAMES[date.getMonth()],
        };
      }),
    [currentMonth, currentYear],
  );

  const [period, setPeriod] = useState("monthly");
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  const hasLoadedInitialRef = useRef(false);

  const reportData = useSelector((state) => state.dashboard?.menuReports);
  const menuReportsLoading = useSelector(
    (state) => state.dashboard?.menuReportsLoading,
  );

  const restaurantId = user?.restaurant_id;
  const currency = user?.currency || "GBP";

  const menus = reportData?.menus || [];

  const requestMenuReport = useCallback(
    (targetPeriod = period, year = selectedYear, month = selectedMonth) => {
      const params =
        targetPeriod === "weekly"
          ? { period: "weekly" }
          : { period: "monthly", year, month };

      dispatch(
        fetchMenuReports({
          restaurant_id: restaurantId,
          params,
        }),
      );
    },
    [dispatch, period, restaurantId, selectedMonth, selectedYear],
  );

  useEffect(() => {
    if (!restaurantId || hasLoadedInitialRef.current) return;

    requestMenuReport("monthly", selectedYear, selectedMonth);
    hasLoadedInitialRef.current = true;
  }, [requestMenuReport, restaurantId, selectedMonth, selectedYear]);

  const handlePeriodChange = (value) => {
    setPeriod(value);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    if (value === "weekly") {
      requestMenuReport("weekly");
      return;
    }

    requestMenuReport("monthly", selectedYear, selectedMonth);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    if (period === "monthly") {
      requestMenuReport("monthly", year, selectedMonth);
    }
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);

    if (!hasLoadedInitialRef.current || !restaurantId || period !== "monthly")
      return;

    requestMenuReport("monthly", selectedYear, month);
  };

  const statCards = [
    {
      title: "Total Menu",
      value: reportData?.stats?.total_menu || 0,
      color: COLORS.gold,
    },
    {
      title: "Active Menu",
      value: reportData?.stats?.active_menu || 0,
      color: COLORS.green,
    },
    {
      title: "Inactive Menu",
      value: reportData?.stats?.inactive_menu || 0,
      color: COLORS.red,
    },
  ];

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={dashboardLinks} />}
      pageName={{ title: "Menu Management Reports" }}
      paths={[
        { name: "Dashboard", path: "/dashboard/overview" },
        { name: "Reports & Analytics", path: "/dashboard/revenue-reports" },
        { name: "Menu Management", path: "/dashboard/menu-reports" },
      ]}
    >
      <div className="container mx-auto p-4 md:p-6 space-y-8 md:space-y-10 min-h-screen text-white font-sans">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
          {statCards.map((item) => (
            <StatCard
              key={item.title}
              title={item.title}
              value={item.value}
              color={item.color}
              loading={menuReportsLoading && !reportData}
            />
          ))}
        </div>

        <div className="bg-[#111111] border border-white/5 rounded-xl p-4 md:p-8 space-y-6 md:space-y-8">
          <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center gap-4">
            <h2 className="text-lg md:text-xl font-semibold tracking-tight">
              Menu-wise Sales & Revenue
            </h2>

            <div className="flex flex-wrap items-center gap-3 md:gap-4">
              {period === "monthly" && (
                <div className="relative">
                  <select
                    className="bg-[#1A1A1A] rounded-md px-3 py-2 items-center border border-white/10 text-sm appearance-none pr-8"
                    value={selectedMonth}
                    onChange={(e) => handleMonthChange(Number(e.target.value))}
                  >
                    {recentMonths.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
              )}

              {period === "monthly" && (
                <div className="relative">
                  <select
                    className="bg-[#1A1A1A] rounded-md px-4 py-2 border border-white/10 text-sm font-medium appearance-none pr-8"
                    value={selectedYear}
                    onChange={(e) => handleYearChange(Number(e.target.value))}
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
              )}

              <DashboardRangeTabs
                options={[
                  { label: "Weekly", value: "weekly" },
                  { label: "Monthly", value: "monthly" },
                ]}
                value={period}
                onChange={handlePeriodChange}
                shape="full"
                className="border-white/10"
                buttonClassName="px-4 md:px-5 py-1.5 text-xs"
              />
            </div>
          </div>

          {menuReportsLoading && !reportData ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {Array.from({ length: 8 }).map((_, idx) => (
                <MenuCard key={idx} loading currency={currency} />
              ))}
            </div>
          ) : menus.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {menus.map((menu) => (
                <MenuCard
                  key={menu?.menu_item_id}
                  item={menu}
                  currency={currency}
                />
              ))}
            </div>
          ) : (
            <div className="text-gray-400 text-sm py-8 text-center">
              No menu report data found
            </div>
          )}
        </div>
      </div>
    </Layout2>
  );
}
